import { db } from '../lib/firebase';
import { getMeasurementsInRange } from './podMeasurements';
import { collection, query, where, orderBy, getDocs, Timestamp, doc, getDoc } from 'firebase/firestore';

export interface IndexedPriceResult {
  unitPrice: number;         // Számolt egységár (Ft/kWh vagy EUR/kWh)
  totalCost: number;         // Teljes költség
  totalConsumption: number;  // Teljes fogyasztás kWh-ban
  currency: 'HUF' | 'EUR';   // Pénznem
  eurHufRate?: number;       // EUR/HUF árfolyam (ha HUF pénznem)
  spread: number;            // Spread értéke (eredeti pénznemben)
  matchedIntervals: number;  // Sikeresen összerendelt időszeletek
  unmatchedIntervals: number; // Hiányzó időszeletek
}

/**
 * Lekérdezi az MNB EUR/HUF középárfolyamát egy adott hónapra
 * Alapértelmezett: https://api.exchangerate-api.com/v4/latest/EUR
 */
async function getEurHufRate(year: number, month: number): Promise<number> {
  try {
    const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');
    const data = await response.json();
    return data.rates.HUF || 400; // Fallback: 400 Ft/EUR
  } catch (error) {
    console.warn('Could not fetch EUR/HUF rate, using fallback 400');
    return 400;
  }
}

/**
 * Kiszámítja az indexált egységárat egy POD-ra egy adott hónapra
 * 
 * 1. Lekérdezi a HUPX DAM adatokat (15 perces árak)
 * 2. Lekérdezi a POD 15 perces fogyasztási méréseit
 * 3. Összerendeli őket időbélyeg alapján
 * 4. Súlyozott átlagot számol: Σ(consumption × Price) / Σ(consumption)
 */
export async function calculateIndexedUnitPrice(
  podId: string,
  year: number,
  month: number,
  paymentCurrency: 'HUF' | 'EUR',
  spreadStr?: string
): Promise<IndexedPriceResult | null> {
  try {
    const monthKey = `${year}-${String(month).padStart(2, '0')}`;
    console.log(`🔢 Calculating indexed price for POD ${podId}, ${monthKey}`);

    // 1. HUPX adatok lekérdezése
    const hupxDocRef = doc(db, 'hupx_data', monthKey);
    const hupxDoc = await getDoc(hupxDocRef);
    
    if (!hupxDoc.exists()) {
      console.warn(`No HUPX data found for ${monthKey}`);
      throw new Error(`Nincs HUPX adat a ${monthKey} hónapra. Futtasd le a HUPX fetcher-et!`);
    }

    const hupxData = hupxDoc.data();
    const hupxRecords: any[] = hupxData.data?.data || [];
    
    if (hupxRecords.length === 0) {
      console.warn(`HUPX data empty for ${monthKey}`);
      throw new Error(`HUPX adatok üresek a ${monthKey} hónapra.`);
    }

    console.log(`📊 HUPX records loaded: ${hupxRecords.length}`);

    // HUPX adatokat map-eljük: "YYYY-MM-DD HH:mm" -> Price
    // A HUPX adatokban: DeliveryDay = "2026-05-31T00:00:00Z", Hour = "1" (1-96, QH)
    const hupxPriceMap = new Map<string, number>();
    
    for (const record of hupxRecords) {
      const dayStr = record.DeliveryDay?.split('T')[0]; // "2026-05-31"
      const qh = parseInt(record.Hour || record.ProductQH, 10); // 1-96
      
      if (!dayStr || isNaN(qh)) continue;
      
      // QH -> idő: 1 = 00:00, 2 = 00:15, ..., 96 = 23:45
      const hour = Math.floor((qh - 1) / 4);
      const minute = ((qh - 1) % 4) * 15;
      const timeKey = `${dayStr} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      
      hupxPriceMap.set(timeKey, parseFloat(record.Price));
    }

    console.log(`🗺️ HUPX price map created with ${hupxPriceMap.size} entries`);

    // 2. POD mérések lekérdezése az adott hónapra
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0, 23, 59, 59); // Hónap utolsó napja
    
    // v1 measurements / v2 napi values[] – ugyanaz a negyedórás lista (podMeasurements adapter)
    const rows = await getMeasurementsInRange(podId, startDate, endDate);
    const measurements: { timeKey: string; consumption: number }[] = [];

    rows.forEach((data) => {
      const ts = data.timestamp.toDate();
      const timeKey = `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, '0')}-${String(ts.getDate()).padStart(2, '0')} ${String(ts.getHours()).padStart(2, '0')}:${String(ts.getMinutes()).padStart(2, '0')}`;
      
      measurements.push({
        timeKey,
        consumption: data.consumption || 0
      });
    });

    console.log(`📈 POD measurements loaded: ${measurements.length}`);

    if (measurements.length === 0) {
      console.warn(`No measurements found for POD ${podId} in ${monthKey}`);
      throw new Error(`Nincs mérési adat a ${podId} POD-hoz a ${monthKey} hónapban.`);
    }

    // 3. Összerendelés és számítás
    let totalCost = 0;           // EUR (consumption[MWh] × Price[EUR/MWh])
    let totalConsumption = 0;  // kWh
    let matchedIntervals = 0;
    let unmatchedIntervals = 0;

    for (const m of measurements) {
      const price = hupxPriceMap.get(m.timeKey);
      
      if (price !== undefined) {
        // consumption kWh -> MWh (osztás 1000-tel)
        // cost = consumption[kWh] / 1000 × Price[EUR/MWh] = EUR
        totalCost += (m.consumption / 1000) * price;
        totalConsumption += m.consumption;
        matchedIntervals++;
      } else {
        unmatchedIntervals++;
      }
    }

    console.log(`✅ Matched: ${matchedIntervals}, ❌ Unmatched: ${unmatchedIntervals}`);
    console.log(`💰 Total cost: ${totalCost.toFixed(2)} EUR, Total consumption: ${totalConsumption.toFixed(2)} kWh`);

    if (totalConsumption === 0) {
      console.warn('Total consumption is zero');
      throw new Error(`A ${podId} POD összes fogyasztása nulla a ${monthKey} hónapban.`);
    }

    // 4. Indexált egységár számítása EUR/kWh-ban
    const indexedPriceEURperKWh = totalCost / totalConsumption;
    console.log(`📊 Indexed price (EUR/kWh): ${indexedPriceEURperKWh.toFixed(6)}`);

    // 5. Pénznem és spread kezelése
    const spread = parseFloat((spreadStr || '0').replace(',', '.')) || 0;
    let finalUnitPrice: number;
    let eurHufRate: number | undefined;

    if (paymentCurrency === 'HUF') {
      // EUR/kWh -> Ft/kWh, majd spread hozzáadása Ft-ban
      eurHufRate = await getEurHufRate(year, month);
      finalUnitPrice = (indexedPriceEURperKWh * eurHufRate) + spread;
      console.log(`💱 EUR/HUF rate: ${eurHufRate}, Spread: ${spread} Ft/kWh`);
      console.log(`💰 Final unit price (HUF/kWh): ${finalUnitPrice.toFixed(4)}`);
    } else {
      // EUR pénznem: spread-et is EUR-ra kell váltani
      // spread Ft/kWh -> EUR/kWh (osztás árfolyammal)
      eurHufRate = await getEurHufRate(year, month);
      const spreadEUR = spread / eurHufRate;
      finalUnitPrice = indexedPriceEURperKWh + spreadEUR;
      console.log(`💱 EUR/HUF rate: ${eurHufRate}, Spread: ${spread} Ft/kWh = ${spreadEUR.toFixed(6)} EUR/kWh`);
      console.log(`💰 Final unit price (EUR/kWh): ${finalUnitPrice.toFixed(6)}`);
    }

    return {
      unitPrice: finalUnitPrice,
      totalCost,
      totalConsumption,
      currency: paymentCurrency,
      eurHufRate,
      spread,
      matchedIntervals,
      unmatchedIntervals
    };

  } catch (error) {
    console.error('Error calculating indexed price:', error);
    return null;
  }
}
